import { LocalStorageKey } from '@/enums/localStorageKey';
import { setHasSlotSession } from '@/lib/store/casino/actions';
import { DetailedBettingSession } from '@/models/betting-session/detailed';
import { GamingAggregatorId } from '@/models/gaming-aggregator/id';
import { GenericError } from '@/models/generic-error';
import { CreateBettingSessionInputDto } from '@/services/dto/betiing-session/create-betting-session-input.dto';
import { usePathname } from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { AuthService } from '@starsoft/common/services';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { useBettingSessionTimePlayed } from '../useBettingSessionTimePlayed';
import { fetchCreateBettingSession } from './service';

export function useCreateBettingSession() {
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const { bettingSessionTimePlayed } = useBettingSessionTimePlayed();
  const pathname = usePathname();
  const [sessionId, setSessionId] = useState<Nullable<string>>(null);
  const unloadEventRef = useRef<((event: BeforeUnloadEvent) => void) | null>(
    null,
  );

  const onSuccess = useCallback(
    (data: DetailedBettingSession, variables: CreateBettingSessionInputDto) => {
      sessionStorage.setItem(
        LocalStorageKey.BettingSessionId,
        data?.id?.toString() ?? '',
      );
      setSessionId(data?.id?.toString());

      // Remove existing event listener if it exists
      if (unloadEventRef.current) {
        window.removeEventListener('beforeunload', unloadEventRef.current);
      }

      const unloadEvent = (event: BeforeUnloadEvent) => {
        event.returnValue = false;

        bettingSessionTimePlayed({
          sessionId: data?.id?.toString(),
        });
        sessionStorage.removeItem(LocalStorageKey.BettingSessionId);
      };

      unloadEventRef.current = unloadEvent;
      window.addEventListener('beforeunload', unloadEvent);

      if (
        variables?.aggregatorId?.toString() ===
        GamingAggregatorId.Softswiss?.toString()
      ) {
        dispatch(setHasSlotSession(true));
      }

      if (
        variables?.aggregatorId?.toString() ===
        GamingAggregatorId.Betby?.toString()
      ) {
        AuthService.setBetbyToken(data?.gateway?.token);
      }
    },
    [bettingSessionTimePlayed, dispatch],
  );

  const {
    mutate,
    mutateAsync,
    variables,
    isPending: isLoading,
    error,
    data,
    isError,
    reset,
  } = useMutation<
    DetailedBettingSession,
    AxiosError<GenericError>,
    CreateBettingSessionInputDto
  >({
    mutationFn: fetchCreateBettingSession,
    onSuccess,
    onError: error => {
      console.error('Error', error);
    },
  });

  function handleBettingSessionTimePlayed() {
    if (!sessionId) {
      return undefined;
    }

    const handleUnloadEvent = () => {
      bettingSessionTimePlayed({
        sessionId,
      });
      setSessionId(null);
    };

    // Return the cleanup function directly
    return function cleanup() {
      handleUnloadEvent();
      // Clean up the beforeunload event listener
      if (unloadEventRef.current) {
        window.removeEventListener('beforeunload', unloadEventRef.current);
        unloadEventRef.current = null;
      }
    };
  }

  useEffect(handleBettingSessionTimePlayed, [
    sessionId,
    pathname,
    bettingSessionTimePlayed,
  ]);

  const onTokenExpired = useCallback(async () => {
    const response = await mutateAsync(variables);
    onSuccess(response, variables);
    return response?.gateway?.token;
  }, [mutateAsync, variables, onSuccess]);

  return {
    createBettingSession: mutate,
    createBettingSessionAsync: mutateAsync,
    onTokenExpired,
    isLoading,
    reset,
    error,
    isError,
    data,
    onUnmount: handleBettingSessionTimePlayed,
  };
}
